// AdherentPassageCeinture.jsx (redesign)
import React, { useState, useEffect, useMemo } from "react";
import {
  getBeltHistoryByAdherent,
  deleteBeltHistory,
  getBeltsList,
  addBeltHistory,
  editBeltHistory,
} from "../../../services/beltServices";
import { getAdherentById } from "../../../services/adherentServices";

import Button from "../../../components/Button/Button";
import ButtonEdit from "../../../components/Button/ButtonEdit";
import ButtonDelete from "../../../components/Button/ButtonDelete";
import ButtonDiplome from "../../../components/Button/ButtonDiplome";

import { FaSave, FaTimes, FaPlus } from "react-icons/fa";
import { toast } from "react-toastify";

const defaultAvatar = "/img/user_avatar.jpg";

const AdherentPassageCeinture = ({ adherentId, onBeltHistoryChange }) => {
  // Identity
  const [adherent, setAdherent] = useState(null);

  // Data
  const [belts, setBelts] = useState([]);
  const [histories, setHistories] = useState([]);

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(false);
  const [currentEditingId, setCurrentEditingId] = useState(null);
  const [selectedBelt, setSelectedBelt] = useState("");
  const [datePassage, setDatePassage] = useState("");

  const [loading, setLoading] = useState(true);

  // Fetch adherent + belts + history
  useEffect(() => {
    if (!adherentId) return;

    (async () => {
      try {
        setLoading(true);

        const [beltsList, adherentDetails] = await Promise.all([
          getBeltsList(),
          getAdherentById(adherentId),
        ]);

        setBelts(beltsList || []);
        setAdherent(adherentDetails || null);

        await loadBeltHistories();
      } catch (error) {
        console.error("Chargement initial (belts/adherent) échoué:", error);
        toast.error("Erreur de chargement des informations de l'adhérent.");
      } finally {
        setLoading(false);
      }
    })();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adherentId]);

  // Charge l’historique et trie par date décroissante
  const loadBeltHistories = async () => {
    try {
      const data = await getBeltHistoryByAdherent(adherentId);

      const sorted = (data || []).slice().sort((a, b) => {
        const da = new Date(a.date_passage).getTime();
        const db = new Date(b.date_passage).getTime();
        return db - da;
      });

      setHistories(sorted);
    } catch (error) {
      console.error("Erreur lors de la récupération de l'historique:", error);
      toast.error("Impossible de charger l'historique des passages.");
    }
  };

  // UI helpers
  const currentBeltName = useMemo(
    () => histories?.[0]?.BeltColor?.nom || "—",
    [histories]
  );

  const currentBeltImage = useMemo(
    () =>
      histories?.[0]?.BeltColor?.belt_url
        ? `/img/belt/${histories[0].BeltColor.belt_url}`
        : null,
    [histories]
  );

  const toAbsolute = (u) => {
    if (!u) return "";

    const s = String(u).trim();

    if (
      /^https?:\/\//i.test(s) ||
      s.startsWith("data:") ||
      s.startsWith("blob:")
    ) {
      return s;
    }

    if (!s.startsWith("/uploads")) return s;

    const base = process.env.REACT_APP_API_URL?.replace(/\/$/, "") || "";
    return `${base}${s}`;
  };

  const resetForm = () => {
    setSelectedBelt("");
    setDatePassage("");
    setEditing(false);
    setCurrentEditingId(null);
  };

  const handleAddClick = () => {
    resetForm();

    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, "0");
    const dd = String(today.getDate()).padStart(2, "0");

    setDatePassage(`${yyyy}-${mm}-${dd}`);
    setShowForm(true);
  };

  const handleCancel = () => {
    resetForm();
    setShowForm(false);
  };

  const handleEditClick = (row) => {
    setSelectedBelt(row.belt_id);

    const d = new Date(row.date_passage);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");

    setDatePassage(`${yyyy}-${mm}-${dd}`);
    setCurrentEditingId(row.id);
    setEditing(true);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer ce passage de grade ?")) return;

    try {
      await deleteBeltHistory(id);
      toast.success("Passage de grade supprimé.");
      await loadBeltHistories();
      onBeltHistoryChange?.();
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de la suppression.");
    }
  };

  const handleSave = async () => {
    if (!selectedBelt || !datePassage) {
      toast.error("Veuillez sélectionner une ceinture et une date.");
      return;
    }

    try {
      await addBeltHistory({
        adherent_id: adherentId,
        belt_id: selectedBelt,
        date_passage: datePassage,
      });

      toast.success("Passage de grade ajouté.");
      await loadBeltHistories();
      onBeltHistoryChange?.();
      setShowForm(false);
      resetForm();
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de l'ajout du passage.");
    }
  };

  const handleUpdate = async () => {
    if (!selectedBelt || !datePassage) {
      toast.error("Veuillez sélectionner une ceinture et une date.");
      return;
    }

    try {
      await editBeltHistory(currentEditingId, {
        belt_id: selectedBelt,
        date_passage: datePassage,
      });

      toast.success("Passage de grade mis à jour.");
      await loadBeltHistories();
      onBeltHistoryChange?.();
      setShowForm(false);
      resetForm();
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de la mise à jour.");
    }
  };

  return (
    <div className="mx-auto max-w-[920px] font-montserrat text-slate-800 dark:text-slate-100">
      {/* Carte identité adhérent */}
      <div className="rounded-2xl ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/90 dark:bg-slate-900/40 p-4 sm:p-5">
        <div className="flex items-center gap-4">
          <img
            src={
              toAbsolute(adherent?.photo_url) ||
              toAbsolute(adherent?.photo) ||
              defaultAvatar
            }
            onError={(e) => {
              e.currentTarget.src = defaultAvatar;
            }}
            alt={`${adherent?.prenom || ""} ${adherent?.nom || ""}`}
            className="h-16 w-16 sm:h-20 sm:w-20 rounded-full object-cover ring-2 ring-white dark:ring-slate-800 shadow"
          />

          <div className="min-w-0">
            <h3 className="text-lg sm:text-xl font-semibold truncate">
              {adherent ? `${adherent.nom} ${adherent.prenom}` : "—"}
            </h3>

            <div className="mt-1 flex flex-wrap items-center gap-3 text-sm">
              <span className="inline-flex items-center rounded-md px-2 py-0.5 bg-slate-100 dark:bg-slate-800/60 ring-1 ring-slate-200/70 dark:ring-slate-700/70">
                Ceinture actuelle&nbsp;:
                <strong className="ml-1">{currentBeltName}</strong>
              </span>

              {currentBeltImage && (
                <img
                  src={currentBeltImage}
                  alt={currentBeltName}
                  className="h-4"
                />
              )}
            </div>
          </div>

          <div className="ml-auto">
            {!showForm ? (
              <Button
                icon={FaPlus}
                text="Ajouter un passage"
                onClick={handleAddClick}
                className="!rounded-xl gap-2"
              />
            ) : (
              <Button
                text="Annuler"
                icon={FaTimes}
                onClick={handleCancel}
                className="!rounded-xl !bg-slate-100 !text-slate-900 hover:!bg-slate-200 dark:!bg-slate-800 dark:!text-slate-100 dark:hover:!bg-slate-700"
              />
            )}
          </div>
        </div>

        {/* Formulaire création / édition */}
        {showForm && (
          <div className="mt-4 rounded-xl border border-slate-200 dark:border-slate-700 p-3 sm:p-4 bg-white dark:bg-slate-900">
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_auto] gap-3 sm:gap-4 items-end">
              <div>
                <label className="block text-xs mb-1 opacity-70">
                  Ceinture
                </label>

                <select
                  className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                  value={selectedBelt}
                  onChange={(e) => setSelectedBelt(e.target.value)}
                >
                  <option value="">Sélectionnez une ceinture</option>
                  {belts.map((belt) => (
                    <option key={belt.id} value={belt.id}>
                      {belt.nom}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs mb-1 opacity-70">Date</label>

                <input
                  type="date"
                  className="rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                  value={datePassage}
                  onChange={(e) => setDatePassage(e.target.value)}
                />
              </div>

              <div className="flex gap-2">
                {editing ? (
                  <Button
                    text="Mettre à jour"
                    icon={FaSave}
                    onClick={handleUpdate}
                    className="!rounded-xl gap-2"
                  />
                ) : (
                  <Button
                    text="Valider"
                    icon={FaSave}
                    onClick={handleSave}
                    className="!rounded-xl gap-2"
                  />
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Tableau des passages */}
      <div className="mt-4 overflow-hidden rounded-2xl border border-slate-200 dark:border-slate-700 bg-white/90 dark:bg-slate-900/40">
        <table className="min-w-full">
          <thead>
            <tr className="bg-slate-50/80 dark:bg-slate-900/80 uppercase tracking-wide text-xs">
              {["Date de passage", "Ceinture", "Actions"].map((h) => (
                <th
                  key={h}
                  className="px-3 py-3 text-left font-semibold text-slate-700 dark:text-white"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>

          <tbody className="divide-y divide-slate-100 dark:divide-slate-800 text-sm">
            {loading && (
              <tr>
                <td
                  colSpan={3}
                  className="px-3 py-6 text-center text-slate-500 dark:text-slate-400"
                >
                  Chargement…
                </td>
              </tr>
            )}

            {!loading && histories.length === 0 && (
              <tr>
                <td
                  colSpan={3}
                  className="px-3 py-6 text-center text-slate-500 dark:text-slate-400"
                >
                  Aucun passage enregistré pour l’instant.
                </td>
              </tr>
            )}

            {histories.map((row) => {
              const belt = row?.BeltColor;
              const beltName = belt?.nom || "Inconnu";

              const beltImg = belt?.belt_url
                ? `/img/belt/${belt.belt_url}`
                : null;

              const dateStr = row?.date_passage
                ? new Date(row.date_passage).toLocaleDateString("fr-FR")
                : "—";

              const ordre = Number(belt?.ordre);
              const showDiplome =
                Number.isFinite(ordre) && ordre >= 2 && ordre <= 13;

              const adherentNom = row?.Adherent?.nom || adherent?.nom || "";
              const adherentPrenom =
                row?.Adherent?.prenom || adherent?.prenom || "";
              const dateNaissance =
                row?.Adherent?.date_naissance ||
                adherent?.date_naissance ||
                null;

              return (
                <tr
                  key={row.id}
                  className="transition-colors hover:bg-slate-50/70 dark:hover:bg-slate-800/40"
                >
                  <td className="px-3 py-3 whitespace-nowrap">{dateStr}</td>

                  <td className="px-3 py-3">
                    <div className="flex items-center gap-2">
                      {beltImg && (
                        <img src={beltImg} alt={beltName} className="h-10" />
                      )}
                      <span>{beltName}</span>
                    </div>
                  </td>

                  <td className="px-3 py-3">
                    <div className="flex flex-wrap items-center gap-2">
                      {showDiplome && (
                        <ButtonDiplome
                          adherentId={adherentId}
                          datePassage={row.date_passage}
                          beltImage={belt?.belt_url}
                          beltName={beltName}
                          adherentNom={adherentNom}
                          adherentPrenom={adherentPrenom}
                          dateNaissance={dateNaissance}
                        />
                      )}

                      <ButtonEdit
                        onClick={() => handleEditClick(row)}
                        hoverLabel="Modifier"
                      />

                      <ButtonDelete
                        onClick={() => handleDelete(row.id)}
                        hoverLabel="Supprimer"
                      />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdherentPassageCeinture;